import appContext from "@builder.io/app-context";
import {
  APIOperations,
  ResourceType,
  registerDataPlugin,
} from "@builder.io/data-plugin-tools";
import { Builder } from "@builder.io/react";
import { isString } from "lodash";
import { z } from "zod";
import { BuilderType } from "./BuilderType";
import { LinkToYouleap } from "./LinkToYouleap";
import { appmixerType } from "./appmixerTypeToBuilderType";
import { IColumn } from "./column.interface";
import { getAllPaths } from "./getAllPaths";

const builderTypeString: BuilderType = "string";

function getYouleapFieldEnumOptions(data: Record<string, unknown>) {
  const allPaths = getAllPaths(data);

  return allPaths.map((pathArray) => {
    return {
      label: pathArray.join("."),
      helperText: `state → ${pathArray.join(" → ")}`,
      value: `{{state.${pathArray.join(".")}}}`,
    };
  });
}

const flowsResponseSchema = z.array(
  z.object({
    id: z.string(),
    name: z.string(),
    inputSchema: z.array(
      z.object({
        name: z.string(),
        type: appmixerType,
      })
    ),
    invocationUrl: z.string(),
    description: z.string().optional(),
  })
);

const PRODUCTION_BASE_URL = "https://gateway.youleap.io";

async function registerYouleapActions(baseUrl: string) {
  const workflowsResponse = await fetch(`${baseUrl}/workflow`);

  const workflows = flowsResponseSchema.parse(await workflowsResponse.json());

  for (const workflow of workflows) {
    const { name, inputSchema, invocationUrl, description, id } = workflow;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
    appContext.registerAction({
      name: `Youleap - ${name}`,
      id,
      kind: "function",
      helperText: description ?? "Execute your Youleap function.",
      inputs: () =>
        inputSchema.map((input) => {
          const { name } = input;

          const youleapFieldEnumOptions = getYouleapFieldEnumOptions(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
            appContext.designerState.stateOverrides.toJSON() as Record<
              string,
              unknown
            >
          );

          return {
            name,
            type: builderTypeString,
            enum: youleapFieldEnumOptions,
            friendlyName: name,
          };
        }),
      toJs: (body: BodyInit) => {
        /**
         * TODO: somehow get the body to not be only `{}`, but get the params that
         * the user passed to the action
         */
        return `
        console.log("${invocationUrl} is here, ${JSON.stringify(body)}");
        fetch("${invocationUrl}", {
          method: "POST",
          body: {},
        })`;
      },
    });
  }
}

Builder.register("appTab", {
  name: "Youleap",
  path: "youleap",
  icon: "https://youleap.io/icon.svg",
  component: LinkToYouleap,
});

void registerDataPlugin(
  {
    name: "Youleap",
    id: "@builder.io/plugin-youleap-v1",
    icon: "https://youleap.io/icon.svg",
    settings: [
      {
        name: "organizationId",
        friendlyName: "Organization ID",
        type: "string",
        required: true,
      },
      {
        name: "baseUrl",
        friendlyName: "Youleap API URL",
        type: "string",
        required: false,
      },
    ],
    ctaText: `Connect Youleap Data`,
  },
  async (settings): Promise<APIOperations> => {
    const baseUrl =
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
      (settings.get("baseUrl") as string | undefined) ?? PRODUCTION_BASE_URL;
    if (!isString(baseUrl)) {
      throw new Error("baseUrl not found");
    }
    await registerYouleapActions(baseUrl);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
    const organizationId = settings.get("organizationId");
    if (!isString(organizationId)) {
      throw new Error("organizationId not found");
    }

    return {
      async getResourceTypes() {
        const bases = await fetch(
          `${baseUrl}/base/query/organization/${organizationId}`
        );
        const parsedBases =
          (await bases.json()) as Array<IOrganizationsBasesHierarchyResponseDto>;

        return parsedBases.flatMap<ResourceType>((base) => {
          return base.tables.map((table) => {
            const { name, id, columns } = table;
            return {
              id,
              name,
              description: `${base.name} → ${name}`,
              canPickEntries: true,
              inputs: () => {
                const youleapFieldEnumOptions = getYouleapFieldEnumOptions(
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
                  appContext.designerState.stateOverrides.toJSON() as Record<
                    string,
                    unknown
                  >
                );

                return [
                  {
                    name: "id",
                    friendlyName: "id",
                    type: "string",
                    enum: youleapFieldEnumOptions,
                  },
                  ...columns.map((column) => {
                    return {
                      name: column.name,
                      friendlyName: column.displayName,
                      type: "string",
                      enum: youleapFieldEnumOptions,
                    };
                  }),
                ];
              },
              toUrl: (fields: { entry?: string } & Record<string, unknown>) => {
                const rowId = fields.entry;
                if (rowId != null) {
                  return `${baseUrl}/base/table/${id}/query/find/many`;
                }

                const queryParamsPrefix =
                  Object.keys(fields).length > 0 ? "?" : "";

                const queryParams = Object.entries(fields)
                  .map(([fieldName, fieldValue]) => {
                    return `${fieldName}=${String(fieldValue)}`;
                  })
                  .join("&");

                return `${baseUrl}/base/table/${id}/query/find/many${queryParamsPrefix}${queryParams}`;
              },
            };
          });
        });
      },
      async getEntriesByResourceType(typeId: string) {
        const results = await fetch(
          `${baseUrl}/base/table/${typeId}/query/find/many`,
          {
            method: "POST",
          }
        );

        const parsedResult = (await results.json()) as Array<
          Record<string, unknown>
        >;

        return parsedResult.map((item) => {
          const parsedItem = z.object({ id: z.string() }).parse(item);
          return {
            id: parsedItem.id,
            name: parsedItem.id,
          };
        });
      },
    };
  }
);

interface IBaseResponseDto {
  id: string;
  updatedAt: Date;
  createdAt: Date;
  organizationId?: string;
  name?: string;
  displayName?: string;
}

interface IOrganizationsBasesHierarchyResponseDto extends IBaseResponseDto {
  tables: Array<IOrganizationsBasesHierarchyTableResponseDto>;
}
interface IOrganizationsBasesHierarchyTableResponseDto {
  id: string;
  name: string;
  displayName: string;
  color: string;
  columns: Array<IColumn>;
}
